import React, { useState, useEffect } from "react";

function TimeSheet() {
  // Define an array of days for easier mapping
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  // Initialize state for the work log with empty entries for each day
  const [workLog, setWorkLog] = useState(
    days.map((day) => ({
      day,
      startTime: "",
      finishTime: "",
      description: "",
      hours: 0,
    }))
  );

  // Initialize state for the total hours worked
  const [totalHoursWorked, setTotalHoursWorked] = useState(0);

  // Function to calculate hours between a start and finish time
  const calculateDailyHours = (start, finish) => {
    // If either time is missing, return 0
    if (!start || !finish) {
      return 0;
    }

    const startMinutes = parseInt(start.split(":")[0]) * 60 + parseInt(start.split(":")[1]);
    const finishMinutes = parseInt(finish.split(":")[0]) * 60 + parseInt(finish.split(":")[1]);

    // Handle cases where the finish time is on the next day
    if (finishMinutes < startMinutes) {
      // Add 24 hours (1440 minutes) to the finish time
      return ((finishMinutes + 1440) - startMinutes) / 60;
    }

    // Calculate the difference in hours, rounded to two decimal places
    const hours = (finishMinutes - startMinutes) / 60;
    return Math.round(hours * 100) / 100;
  };

  // Handle changes to a time input field
  const handleTimeChange = (index, field, value) => {
    // Create a copy of the current work log to avoid direct state mutation
    const newWorkLog = [...workLog];
    newWorkLog[index][field] = value;

    // Recalculate the hours for the specific day
    const newHours = calculateDailyHours(newWorkLog[index].startTime, newWorkLog[index].finishTime);
    newWorkLog[index].hours = newHours;

    // Update the state with the new work log
    setWorkLog(newWorkLog);
  };

  // Recalculate total hours whenever the work log changes
  useEffect(() => {
    const total = workLog.reduce((sum, day) => sum + day.hours, 0);
    setTotalHoursWorked(Math.round(total * 100) / 100);
  }, [workLog]);

  return (
    <>
      <style>
        {`
          /* CSS for print functionality */
          @media print {
            body * {
              visibility: hidden; /* Hide all elements on the page by default */
            }
            #TimeSheet, #TimeSheet * {
              visibility: visible; /* Make only the form and its children visible */
              color: initial !important; /* Revert to default text color */
              font-weight: initial !important; /* Revert to default font weight */
            }
            #TimeSheet {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 0;
              box-shadow: none;
              border: none;
            }
            /* Set up page margins for printing */
            @page {
              size: A4;
              margin: 20mm; /* Add a reasonable margin to prevent content from being cut off */
            }
            /* Hide the print button when printing */
            .print-button {
              display: none;
            }
            /* Ensure input fields and textareas are visible and readable when printed */
            input, textarea {
                border: 1px solid #000 !important;
                background-color: transparent !important;
            }
          }
        `}
      </style>

      <div id="TimeSheet" className="p-4 border rounded-lg bg-white shadow-md font-sans">
        <h3 className="text-2xl font-semibold mb-4 text-gray-800">Employee Time Sheet</h3>

        {/* Submission Info */}
        <p className="text-lg mb-4 text-gray-600">
          Timesheets must be submitted by 5:00 pm every Friday.
        </p>

        <form>
          {/* Employee Details */}
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-lg font-medium text-gray-700" htmlFor="employeeName">
                Employee Name:
              </label>
              <input
                type="text"
                id="employeeName"
                className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter employee name"
              />
            </div>
            <div>
              <label className="block text-lg font-medium text-gray-700" htmlFor="employeeID">
                Employee ID:
              </label>
              <input
                type="text"
                id="employeeID"
                className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter employee ID"
              />
            </div>
          </div>

          {/* Week Commencing */}
          <div className="mb-4">
            <label className="block text-lg font-medium text-gray-700" htmlFor="weekCommencing">
              Week Commencing:
            </label>
            <input
              type="date"
              id="weekCommencing"
              className="p-3 border border-gray-300 rounded-lg w-full md:w-1/3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Weekly Work Logs (Table for each day) */}
          <h4 className="text-xl font-semibold mb-4 text-gray-800">Work Log</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {workLog.map((dayEntry, index) => (
              <div key={dayEntry.day} className="mb-4 p-4 border rounded-lg shadow-sm">
                <h5 className="text-lg font-medium mb-2 text-gray-700">{dayEntry.day}:</h5>
                <table className="w-full table-fixed">
                  <thead>
                    <tr>
                      <th className="border p-2 text-sm text-gray-600 text-left">Start</th>
                      <th className="border p-2 text-sm text-gray-600 text-left">Finish</th>
                      <th className="border p-2 text-sm text-gray-600 text-left">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-2">
                        <input
                          type="time"
                          value={dayEntry.startTime}
                          onChange={(e) => handleTimeChange(index, "startTime", e.target.value)}
                          className="p-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                      <td className="border p-2">
                        <input
                          type="time"
                          value={dayEntry.finishTime}
                          onChange={(e) => handleTimeChange(index, "finishTime", e.target.value)}
                          className="p-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                      <td className="border p-2">
                        <input
                          type="number"
                          value={dayEntry.hours}
                          readOnly
                          className="p-2 border border-gray-300 rounded-lg w-full bg-gray-100 cursor-not-allowed"
                        />
                      </td>
                    </tr>
                    <tr>
                        <td colSpan="3" className="border p-2">
                            <label className="sr-only" htmlFor={`description-${dayEntry.day}`}>Description of Work</label>
                            <input
                              type="text"
                              id={`description-${dayEntry.day}`}
                              value={dayEntry.description}
                              onChange={(e) => {
                                const newWorkLog = [...workLog];
                                newWorkLog[index].description = e.target.value;
                                setWorkLog(newWorkLog);
                              }}
                              className="p-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Work description"
                            />
                        </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Expenses and Allowances */}
          <div className="mb-4 mt-6">
            <h5 className="text-lg font-semibold mb-2 text-gray-800">Expenses and Allowances</h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-lg font-medium text-gray-700" htmlFor="expenses">
                  Expenses:
                </label>
                <input
                  type="number"
                  id="expenses"
                  className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter expenses"
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700" htmlFor="allowances">
                  Allowances:
                </label>
                <input
                  type="number"
                  id="allowances"
                  className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter allowances"
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700" htmlFor="nightsOut">
                  Nights Out (EU/UK):
                </label>
                <input
                  type="number"
                  id="nightsOut"
                  className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter nights out count"
                />
              </div>
            </div>
          </div>

          {/* Totals */}
          <h5 className="text-lg font-semibold mb-2 text-gray-800">Totals</h5>
          <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-lg font-medium text-gray-700" htmlFor="totalHoursWorked">
                Total Hours Worked:
              </label>
              <input
                type="number"
                id="totalHoursWorked"
                value={totalHoursWorked}
                readOnly
                className="p-3 border border-gray-300 rounded-lg w-full bg-gray-100 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-lg font-medium text-gray-700" htmlFor="totalOvertimeHours">
                Total Overtime Hours:
              </label>
              <input
                type="number"
                id="totalOvertimeHours"
                className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Total overtime hours"
              />
            </div>
            <div>
              <label className="block text-lg font-medium text-gray-700" htmlFor="totalPayDue">
                Total Pay Due:
              </label>
              <input
                type="number"
                id="totalPayDue"
                className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Total pay due"
              />
            </div>
          </div>

          {/* Signature Fields */}
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-lg font-medium text-gray-700" htmlFor="driverSignature">
                Driver Signature:
              </label>
              <input
                type="text"
                id="driverSignature"
                className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Driver Signature"
              />
            </div>
            <div>
              <label className="block text-lg font-medium text-gray-700" htmlFor="managerSignature">
                Manager Signature:
              </label>
              <input
                type="text"
                id="managerSignature"
                className="p-3 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Manager Signature"
              />
            </div>
          </div>
        </form>

        {/* Print Button */}
        <div className="mt-6 text-right print-button">
          <button
            type="button"
            onClick={() => window.print()}
            className="w-full md:w-64 bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg"
          >
            Print Form
          </button>
        </div>
      </div>
    </>
  );
}

export default TimeSheet;